import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PasswordInput } from "@/components/PasswordInput";
import { ArrowRight, ArrowLeft, Upload, Camera, CheckCircle2, Loader2, Store, Clock, Building2, User, Mail, Lock, Percent, Wallet, Globe } from "lucide-react";
import { toast } from "sonner";
import { startGoogleLogin } from "@/lib/googleAuth";

const STEPS = ["Business details", "Business type", "Product category", "Identity & documents", "Bank account"];

export default function SellerCenter() {
  const { user, login, register, refresh } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("auth"); // auth | register | done
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uploadingKey, setUploadingKey] = useState("");

  const [signin, setSignin] = useState({ email: "", password: "" });
  const creds = signin; const setCreds = setSignin;
  const [form, setForm] = useState({
    name: "", email: "", password: "", business_name: "", phone: "", business_address: "",
    country: "", business_type: "individual", primary_category: "", tax_number: "",
    bank_details: "", mobile_money: "",
  });
  const [docs, setDocs] = useState({ gov_id_doc: "", bank_statement_doc: "", selfie_doc: "", business_cert_doc: "", export_license_doc: "" });

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {}); }, []);

  // Approved sellers / admins go straight to the dashboard.
  useEffect(() => {
    if (user && (user.role === "admin" || (user.role === "seller" && user.seller_status === "approved"))) navigate("/seller");
  }, [user, navigate]);

  // A buyer (or rejected seller) who's already signed in jumps straight into the wizard, prefilled.
  const booted = useRef(false);
  useEffect(() => {
    if (booted.current || !user) return;
    booted.current = true;
    if (view !== "auth") return; // guest already inside the wizard — don't hijack step state
    if (user.role === "admin" || (user.role === "seller" && user.seller_status === "approved")) return;
    if (user.seller_status === "pending") return;
    setForm((f) => ({ ...f, name: user.name || f.name, email: user.email || f.email, business_name: user.business_name || f.business_name, phone: user.phone || f.phone, country: user.country || f.country }));
    setView("register"); setStep(1);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const doSignIn = async (e) => {
    if (e) e.preventDefault();
    if (!creds.email || !creds.password) { toast.error("Enter your email and password to sign in."); return; }
    setLoading(true);
    try {
      const u = await login(creds.email, creds.password);
      toast.success(`Welcome back, ${u.name}!`);
      if (!u.email_verified) { navigate("/verify", { state: { email: u.email } }); return; }
      navigate(u.role === "seller" || u.role === "admin" ? "/seller" : "/seller/apply");
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid email or password"); }
    setLoading(false);
  };

  // Register (create account) then drop into the onboarding wizard.
  const doRegister = async (e) => {
    if (e) e.preventDefault();
    if (user) { startRegister(); return; }
    if (!creds.email || !creds.password || creds.password.length < 6) {
      toast.error("Enter your email and a password of at least 6 characters."); return;
    }
    setLoading(true);
    try {
      const derivedName = creds.email.split("@")[0];
      await register({ name: derivedName, email: creds.email, password: creds.password, role: "seller" });
      await refresh();
      setForm((f) => ({ ...f, name: derivedName, email: creds.email, password: creds.password }));
      toast.success("Account created — let's set up your store.");
      setView("register"); setStep(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create your account. If you already have one, sign in instead.");
    }
    setLoading(false);
  };

  // Google sign-up: self-branded Google OAuth; stash seller intent so we route to onboarding after callback.
  const googleRegister = async () => {
    localStorage.setItem("xurray_pending_role", "seller");
    try {
      await startGoogleLogin();
    } catch (e) {
      toast.error(e.message || "Google sign-in is unavailable right now");
    }
  };

  const startRegister = () => {
    if (user) { // logged-in buyer becoming a seller — prefill and skip account step
      setForm((f) => ({ ...f, name: user.name || "", email: user.email || "", business_name: user.business_name || "", phone: user.phone || "", country: user.country || "" }));
      setStep(1);
    } else { setStep(1); }
    setView("register");
  };

  // Secure private upload for KYC docs (returns a URL).
  const uploadSecure = (kind, key) => async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/seller/verification/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => ({ ...d, [key]: data.url }));
      toast.success("Uploaded securely");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingKey(""); }
  };

  const uploadBase64 = (key) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setDocs((d) => ({ ...d, [key]: reader.result })); toast.success(`${file.name} attached`); };
    reader.readAsDataURL(file);
  };

  const next = async () => {
    if (step === 1) {
      if (!form.name || !form.email || !form.business_name || !form.phone || !form.country) { toast.error("Please complete all required fields."); return; }
      if (!user) {
        if (!form.password || form.password.length < 6) { toast.error("Choose a password with at least 6 characters."); return; }
        setLoading(true);
        try {
          await register({ name: form.name, email: form.email, password: form.password, role: "seller" });
          await refresh();
        } catch (err) {
          setLoading(false);
          toast.error(err.response?.data?.detail || "Could not create your account. If you already have one, sign in instead.");
          return;
        }
        setLoading(false);
      }
    }
    if (step === 3 && !form.primary_category) { toast.error("Select your main product category."); return; }
    if (step === 4 && (!docs.gov_id_doc || !docs.selfie_doc || !docs.bank_statement_doc)) {
      toast.error("Please upload your government ID, a selfie and your bank statement.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length));
  };

  const submitApplication = async () => {
    if (!form.bank_details.trim()) { toast.error("Enter your bank account details for payouts."); return; }
    setLoading(true);
    try {
      await api.post("/seller/apply", { ...form, full_name: form.name, ...docs });
      await refresh();
      setView("done");
    } catch (err) { toast.error(err.response?.data?.detail || "Submission failed"); }
    setLoading(false);
  };

  // ---------- Confirmation ----------
  if (view === "done" || user?.seller_status === "pending") return (
    <Layout hideAd>
      <div className="max-w-lg mx-auto text-center px-4 py-24">
        <Clock className="w-16 h-16 mx-auto text-amber-500 mb-5" />
        <h1 className="font-display font-black text-3xl text-gray-900">Your application is under review</h1>
        <p className="text-muted-foreground mt-3 text-base">Our team will verify your documents within <strong>24–48 hours</strong>. You'll be notified by email once your Xurray seller account is approved.</p>
        <span data-testid="pending-badge" className="mt-5 inline-block rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-4 py-2">Status: PENDING</span>
        <div className="mt-8"><Button onClick={() => navigate("/")} variant="outline" className="rounded-full">Back to Xurray Mart</Button></div>
      </div>
    </Layout>
  );

  const logo = (
    <Link to="/" className="inline-flex items-center gap-2 mb-8" data-testid="seller-center-logo">
      <img src="/xurray-x-mark.png" alt="Xurray Mart" className="h-10 w-auto" />
      <span className="font-display font-black text-xl tracking-tight">Xurray <span className="text-primary">Mart</span></span>
    </Link>
  );

  // ---------- Sign-in / Register landing (template design) ----------
  if (view === "auth") return (
    <Layout hideAd>
      <div className="bg-white" data-testid="seller-center">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 items-stretch">
          {/* Left — seller sign-up form */}
          <div className="bg-[#eef6f3] px-6 sm:px-10 lg:px-14 py-14 flex flex-col justify-center">
            {logo}
            <p className="flex items-center gap-2 text-[11px] font-extrabold tracking-[0.2em] text-primary uppercase mb-3">
              <span className="inline-block h-px w-7 bg-primary" />Join our marketplace
            </p>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-[#152238] leading-[1.05]">
              Become a Seller on<br />Xurray <span className="text-primary">Mart</span>
            </h1>
            <p className="text-muted-foreground mt-4 text-base max-w-sm">
              The premium marketplace for authentic products, delivered across Europe and Africa.
            </p>

            <form onSubmit={doRegister} className="mt-8 space-y-4 max-w-md">
              <div className="relative">
                <Mail className="w-4 h-4 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input data-testid="seller-signin-email" type="email" required value={creds.email}
                  onChange={(e) => setCreds((s) => ({ ...s, email: e.target.value }))}
                  className="h-12 pl-11 rounded-xl bg-white border-gray-200 focus-visible:ring-primary" placeholder="Email Address" />
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none" />
                <PasswordInput data-testid="seller-signin-password" required value={creds.password}
                  onChange={(e) => setCreds((s) => ({ ...s, password: e.target.value }))}
                  className="h-12 pl-11 rounded-xl bg-white border-gray-200 focus-visible:ring-primary" placeholder="Password" />
              </div>

              <Button data-testid="seller-register-btn" type="submit" disabled={loading}
                className="w-full h-12 rounded-full text-base font-bold shadow-sm">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Register <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>

              <div className="flex items-center gap-3 text-xs text-muted-foreground font-medium">
                <span className="h-px flex-1 bg-gray-200" />OR<span className="h-px flex-1 bg-gray-200" />
              </div>

              <Button data-testid="seller-signin-btn" type="button" variant="outline" disabled={loading} onClick={() => doSignIn()}
                className="w-full h-12 rounded-full text-base font-bold border-primary text-primary hover:bg-primary/5">
                Sign In
              </Button>

              <Button data-testid="seller-google-btn" type="button" variant="outline" disabled={loading} onClick={googleRegister}
                className="w-full h-12 rounded-full text-base font-semibold bg-white border-gray-200 hover:bg-gray-50">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M23.52 12.27c0-.82-.07-1.6-.2-2.36H12v4.47h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.74z" />
                  <path fill="#34A853" d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.88-3c-1.08.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.26v3.1A12 12 0 0 0 12 24z" />
                  <path fill="#FBBC05" d="M5.27 14.28a7.2 7.2 0 0 1 0-4.56v-3.1H1.26a12 12 0 0 0 0 10.76l4.01-3.1z" />
                  <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.26 6.62l4.01 3.1C6.22 6.86 8.87 4.75 12 4.75z" />
                </svg>
                Continue with Google
              </Button>

              <div className="text-center pt-1">
                <Link to="/forgot-password" data-testid="seller-forgot-link" className="text-sm text-primary hover:underline font-medium">Forget Password?</Link>
              </div>
            </form>

            {/* Why sell on Xurray Mart */}
            <div className="mt-9 max-w-md grid grid-cols-3 gap-3" data-testid="why-sell-strip">
              {[[Percent, "Low fees", "Just 2.5% per sale"],
                [Wallet, "Fast payouts", "Straight to your bank"],
                [Globe, "EU + Africa", "Reach shoppers in both"]].map(([Icon, title, sub]) => (
                <div key={title} className="rounded-2xl bg-white/70 border border-white px-3 py-4 text-center shadow-sm">
                  <span className="inline-flex w-9 h-9 items-center justify-center rounded-full bg-primary/10 text-primary mb-2"><Icon className="w-4 h-4" /></span>
                  <p className="text-sm font-bold text-[#152238] leading-tight">{title}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{sub}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — hero illustration + feature badges (template image) */}
          <div className="flex items-center justify-center p-6 sm:p-10 lg:p-12">
            <img src="/seller-hero.jpg" alt="Grow your business with Xurray Mart — sell anywhere, anytime"
              className="w-full max-w-2xl h-auto rounded-2xl" data-testid="seller-hero-image" />
          </div>
        </div>
      </div>
    </Layout>
  );

  // ---------- Multi-step registration ----------
  return (
    <Layout hideAd>
      <div className="max-w-xl mx-auto px-5 py-12" data-testid="seller-register-wizard">
        {logo}
        {/* Progress */}
        <div className="flex items-center gap-1.5 mb-2">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < step ? "bg-emerald-500" : "bg-gray-200"}`} />
          ))}
        </div>
        <p className="text-xs text-muted-foreground mb-6">Step {step} of {STEPS.length} · {STEPS[step - 1]}</p>

        <div className="rounded-2xl border bg-white p-6">
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-display font-bold text-xl text-gray-900">Business &amp; contact details</h2>
              <div><Label className="text-sm">Full name</Label><Input data-testid="reg-name" value={form.name} onChange={set("name")} className="mt-1" /></div>
              {!user && <>
                <div><Label className="text-sm">Email address</Label><Input data-testid="reg-email" type="email" value={form.email} onChange={set("email")} className="mt-1" /></div>
                <div><Label className="text-sm">Password</Label><PasswordInput data-testid="reg-password" value={form.password} onChange={set("password")} className="mt-1" /></div>
              </>}
              <div><Label className="text-sm">Business name</Label><Input data-testid="reg-business-name" value={form.business_name} onChange={set("business_name")} className="mt-1" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-sm">Phone</Label><Input data-testid="reg-phone" value={form.phone} onChange={set("phone")} className="mt-1" /></div>
                <div><Label className="text-sm">Country</Label><Input data-testid="reg-country" value={form.country} onChange={set("country")} className="mt-1" /></div>
              </div>
              <div><Label className="text-sm">Business address</Label><Textarea data-testid="reg-address" value={form.business_address} onChange={set("business_address")} className="mt-1" /></div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-display font-bold text-xl text-gray-900">Business type</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[["individual", "Individual", User], ["company", "Registered company", Building2]].map(([val, label, Icon]) => (
                  <button key={val} type="button" data-testid={`reg-type-${val}`} onClick={() => setForm((f) => ({ ...f, business_type: val }))}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-colors ${form.business_type === val ? "border-emerald-500 bg-emerald-50" : "border-gray-200 hover:border-gray-300"}`}>
                    <Icon className={`w-5 h-5 ${form.business_type === val ? "text-emerald-600" : "text-gray-400"}`} />
                    <span className="font-medium text-sm">{label}</span>
                  </button>
                ))}
              </div>
              {form.business_type === "company" && (
                <div className="space-y-4 pt-1">
                  <div><Label className="text-sm">Tax / registration number</Label><Input data-testid="reg-tax" value={form.tax_number} onChange={set("tax_number")} className="mt-1" /></div>
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-dashed cursor-pointer hover:border-primary">
                    <Upload className="w-4 h-4 text-muted-foreground" /><span className="text-sm flex-1">Business registration certificate (optional)</span>
                    {docs.business_cert_doc && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                    <input type="file" data-testid="reg-upload-cert" accept="image/*,.pdf" onChange={uploadBase64("business_cert_doc")} className="hidden" />
                  </label>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-display font-bold text-xl text-gray-900">What do you sell?</h2>
              <div><Label className="text-sm">Primary product category</Label>
                <select data-testid="reg-category" value={form.primary_category} onChange={set("primary_category")}
                  className="mt-1 w-full h-11 rounded-lg border border-gray-300 px-3 bg-white text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                  <option value="">Select a category</option>
                  {categories.map((c) => <option key={c.slug || c.name} value={c.slug || c.name}>{c.name}</option>)}
                </select>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <h2 className="font-display font-bold text-xl text-gray-900">Identity &amp; documents (KYC)</h2>
              <p className="text-xs text-muted-foreground">Stored privately — visible only to our verification team.</p>
              {[["id_document", "gov_id_doc", "Government ID (passport / driver's licence)", "image/*,.pdf", undefined],
                ["selfie", "selfie_doc", "Photo verification (selfie)", "image/*", "user"],
                ["bank_statement", "bank_statement_doc", "Bank statement", "image/*,.pdf", undefined]].map(([kind, key, l, accept, capture]) => (
                <label key={key} className="flex items-center gap-3 p-3 rounded-lg border border-dashed cursor-pointer hover:border-primary">
                  {kind === "selfie" ? <Camera className="w-4 h-4 text-muted-foreground" /> : <Upload className="w-4 h-4 text-muted-foreground" />}
                  <span className="text-sm flex-1">{l}</span>
                  {uploadingKey === key ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : docs[key] && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                  <input type="file" data-testid={`reg-upload-${key}`} accept={accept} capture={capture} onChange={uploadSecure(kind, key)} className="hidden" />
                </label>
              ))}
              <label className="flex items-center gap-3 p-3 rounded-lg border border-dashed cursor-pointer hover:border-primary">
                <Upload className="w-4 h-4 text-muted-foreground" /><span className="text-sm flex-1">Export licence (optional)</span>
                {docs.export_license_doc && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                <input type="file" data-testid="reg-upload-export" accept="image/*,.pdf" onChange={uploadBase64("export_license_doc")} className="hidden" />
              </label>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <h2 className="font-display font-bold text-xl text-gray-900">Bank account for payouts</h2>
              <div><Label className="text-sm">Bank account details</Label><Textarea data-testid="reg-bank" value={form.bank_details} onChange={set("bank_details")} placeholder="Bank name, account name, account number / IBAN" className="mt-1" /></div>
              <div><Label className="text-sm">Mobile money (optional)</Label><Input data-testid="reg-mobile-money" value={form.mobile_money} onChange={set("mobile_money")} className="mt-1" /></div>
            </div>
          )}

          {/* Nav */}
          <div className="flex items-center justify-between mt-7">
            <Button type="button" variant="ghost" data-testid="reg-back"
              onClick={() => (step === 1 ? setView("auth") : setStep((s) => s - 1))} className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-1.5" />{step === 1 ? "Back" : "Previous"}
            </Button>
            {step < STEPS.length ? (
              <Button type="button" data-testid="reg-next" onClick={next} disabled={loading} size="lg" className="rounded-full px-8">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            ) : (
              <Button type="button" data-testid="reg-submit" onClick={submitApplication} disabled={loading} size="lg" className="rounded-full px-8">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit application <CheckCircle2 className="w-4 h-4 ml-1.5" /></>}
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          <Store className="w-4 h-4 inline mr-1 -mt-0.5" />No subscription — just 2.5% commission per sale.
        </p>
      </div>
    </Layout>
  );
}
